import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { format, parseISO } from 'date-fns';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ReceiveReleaseContextDto } from '@/entities/receive';
import { DatePicker, FormField, SimpleSelect } from '@/shared/ui';

import { RECEPTION_VERIFICATION_CODES, RELEASE_GOODS_TEXT_MAX_LENGTH } from '../model/constants';
import type { ReleaseGoodsFormValues } from '../model/types';
import { ReleaseGoodsLookupInput } from './release-goods-lookup-input';

interface ReleaseGoodsHeaderFieldsProps {
  form: UseFormReturn<ReleaseGoodsFormValues>;
  context: ReceiveReleaseContextDto;
  maxReleaseDate: string;
}

/** `yyyy-MM-dd` ↔ `Date`, parsed as local time so the day never shifts. */
const parseDate = (value?: string | null): Date | undefined => {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

/** The legacy `releaseheaderdetails.xhtml` fields, in its order. */
export function ReleaseGoodsHeaderFields({ form, context, maxReleaseDate }: ReleaseGoodsHeaderFieldsProps) {
  const { t } = useTranslation();
  const {
    control,
    register,
    formState: { errors }
  } = form;

  const verificationOptions = useMemo(
    () =>
      context.receptionVerificationCodes?.map(({ code, label }) => ({ value: code, label })) ??
      RECEPTION_VERIFICATION_CODES.map((code) => ({
        value: code,
        label: t(`shipment.receptionVerificationCode.${code}.label`)
      })),
    [context.receptionVerificationCodes, t]
  );

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-7 sm:grid-cols-2">
      <FormField
        label={t('journey.actualDispatchTime.alt.label', 'Actual Date of Release')}
        name="releaseDate"
        error={errors.releaseDate?.message}
        required>
        <Controller
          name="releaseDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              id="releaseDate"
              value={parseDate(field.value) ?? null}
              onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
              dateFormat="dd/MMM/yyyy"
              minDate={parseDate(context.minReleaseDate)}
              maxDate={parseDate(maxReleaseDate)}
            />
          )}
        />
      </FormField>

      <FormField
        label={t('shipment.receptionDocumentNumber.alt1.short', 'Document Number')}
        name="receptionDocumentNumber"
        error={errors.receptionDocumentNumber?.message}>
        <Input
          {...register('receptionDocumentNumber')}
          id="receptionDocumentNumber"
          maxLength={RELEASE_GOODS_TEXT_MAX_LENGTH}
          placeholder={t('shipment.receptionDocumentNumber.alt1.label', 'Document Number (e.g. waybill)')}
        />
      </FormField>

      <FormField
        label={t('shipment.transporterCompanyReleasedTo.label', 'Transport Company or Organization Released to')}
        name="transporterCompanyReleasedTo"
        error={errors.transporterCompanyReleasedTo?.message}
        required>
        <Controller
          name="transporterCompanyReleasedTo"
          control={control}
          render={({ field }) => (
            <ReleaseGoodsLookupInput
              id="transporterCompanyReleasedTo"
              field="TRANSPORTER_COMPANY"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t('journey.transportCompany.desc', 'Transport Company')}
            />
          )}
        />
      </FormField>

      <FormField
        label={t('shipment.driverReleasedTo.label', 'Actual Person Items Release to')}
        name="driverReleasedTo"
        error={errors.driverReleasedTo?.message}
        required>
        <Controller
          name="driverReleasedTo"
          control={control}
          render={({ field }) => (
            <ReleaseGoodsLookupInput
              id="driverReleasedTo"
              field="DRIVER"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t('journey.driverContact.label', 'Driver Contact')}
            />
          )}
        />
      </FormField>

      {/* Display only: the release takes no receiver override — JSF shows it
          read-only and never submits one. */}
      <FormField label={t('dispatch.receiver.desc', 'Receiving Organization Contact')} name="receiver">
        <Input id="receiver" value={context.receiverName || '—'} disabled readOnly />
      </FormField>

      <FormField
        label={t('shipment.receptionVerificationCode.label', 'How was reception verified?')}
        name="receptionVerificationCode"
        error={errors.receptionVerificationCode?.message}
        required>
        <Controller
          name="receptionVerificationCode"
          control={control}
          render={({ field }) => (
            <SimpleSelect
              options={verificationOptions}
              value={field.value}
              // Legacy's select has no empty option; clearing keeps the current code.
              onChange={(value) => value && field.onChange(value)}
              className="w-full"
            />
          )}
        />
      </FormField>

      <FormField
        label={t('shipment.releaseremarks.label', 'Additional details')}
        name="remarks"
        error={errors.remarks?.message}
        className="sm:col-span-2">
        <Textarea
          {...register('remarks')}
          id="remarks"
          rows={2}
          maxLength={RELEASE_GOODS_TEXT_MAX_LENGTH}
          placeholder={t('shipment.releaseremarks.desc', 'Transporter truck type, plate number etc.')}
        />
      </FormField>
    </div>
  );
}
